import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Button, Tooltip, Form, Tag, Checkbox, Avatar, Divider, Spin } from 'antd';
import { EditOutlined, CopyOutlined, DeleteOutlined, PhoneOutlined, WindowsFilled } from '@ant-design/icons';
import { message, Popconfirm } from 'antd';
import dayjs from 'dayjs';
import { BASE_URL, DateFormat,DateTimeFormat } from '../Components/Constant';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import ApiService from '../Components/apiService'; // Import ApiService class
import CreateRecordDrawer from './CreateRecordDrawer';
import CreateRecordModal from './CreateRecordModal';
import { generateBody, formatRecordData, fetchLookupData,convertDateToUserTimezone } from '../Components/Utility';
import ViewAllRecords from '../SetupComponent/ViewAllRecords';
import ListViewTable from "../Record/ListViewTable";
import { useSelector } from 'react-redux';
import isEqual from "lodash/isEqual";
import Cookies from "js-cookie";
import { LookupCell } from './LookupCell';
import { genComponentStyleHook } from 'antd/es/theme/internal';

dayjs.extend(customParseFormat);

const colors = ['blue', 'green', 'red', 'purple', 'orange', 'volcano', 'gold', 'cyan', 'lime', 'pink'];

const getUniqueColor = (index) => {
  // Assign a color based on the index, wrapping around if there are more values than colors
  return colors[index % colors.length];
};

const ChildRecordTable = ({ keyword,
  isGlobalSearch = false,
  fetchCurrency,
  getConvertedValue,
  fieldsData,
  fieldsToDisplay,
  childRecords = [],
  childObjectName,
  onEdit,
  onClone,
  onDelete,
  relatedListId,
  currentRecordId,
  currentObjectName,
  refreshRecords,
  child_object_id,
  isLoading,
  creationView,
  layout,
  pagination,
  highlightFilters,
  filterLogic,
  highlightTooltipMessage = "This record matches the highlight filters.",
  tableProperties,
  getRowStyle,
  component,
}) => {

  const [lookupNames, setLookupNames] = useState({});
  const { id } = useParams();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [objectName, setObjectName] = useState(null);
  const [objectPluralName, setObjectPluralName] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [fieldsDataState, setFieldsDataState] = useState([]); // Define the state for fieldsData
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [lookupOptions, setLookupOptions] = useState([]);
  const [lookupName, setLookupName] = useState('');
  const [lookupFieldName, setLookupFieldName] = useState('');
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loadingData, setLoadingData] = useState(false);  // To track loading state for the additional data
  const [responseData, setResponseData] = useState([]);
  const [currency, setCurrency] = useState('');
  const defaultCurrency = useSelector(state => state.data.orgItem);
  const loginUserCurrency = Cookies.get('userCurrency');
  const fetchCurrencyRef = useRef(false);
  const isMountedRef = useRef(true);
  const security = useSelector(state => state.data.items[0]);


  
  const fetchOrgSetupDetails = useCallback(async () => {
    setCurrency(defaultCurrency?.currency);
  }, [defaultCurrency?.currency]);

  // Call fetchCurrency only once on mount
  // useEffect(() => {
  //   let timeoutId;
    
  //   if (fetchCurrency && !fetchCurrencyRef.current && isMountedRef.current) {
  //     // Use setTimeout to ensure this runs after component is fully mounted
  //     timeoutId = setTimeout(() => {
  //       if (!fetchCurrencyRef.current && isMountedRef.current) {
  //         console.log('Calling fetchCurrency - one time only');
  //         fetchCurrency();
  //         fetchCurrencyRef.current = true;
  //       }
  //     }, 0);
  //   }

  //   return () => {
  //     if (timeoutId) {
  //       clearTimeout(timeoutId);
  //     }
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []); // Intentionally empty - we only want this to run once

  const fetched = useRef(false);

  useEffect(() => {
    if (!fetchCurrencyRef) {
      fetchCurrency();
      fetchCurrencyRef.current = true;
    }
  }, [fetchCurrency]);
  
  useEffect(() => {
    fetchOrgSetupDetails();
  }, [fetchOrgSetupDetails]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);


  // Reorder fieldsData based on fieldsToDisplay
  const orderedFields = isGlobalSearch
  ? fieldsData
  : fieldsToDisplay
      ?.map(fieldName => fieldsData.find(field => field.name === fieldName))
      .filter(Boolean);


  // Define columns dynamically based on fieldsData
  const columns = orderedFields?.map((field, index) => ({
    title: field.label,
    dataIndex: field.name,
    key: field.name,
    width: (field.type === 'address' || field.type === 'multiselect' || field.type === 'image') ? '350px' : '150px',
    sorter: field.name === 'Name' ? (a, b) => (a[field.name]?.localeCompare(b[field.name]) || 0) : undefined,
    sorter: field.type === 'integer' ? (a, b) => a.Num - b.Num : undefined,
    fixed: field.name === 'Name' ? 'left' : undefined,
    onCell: (record, rowIndex) => {
      const result = getRowStyle ? getRowStyle(record, component) : {};
      const style = result.style || {};
      // Only apply background and color, NOT border (border should be row-level)
      return {
        style: {
          backgroundColor: style.backgroundColor,
          color: style.color,
          fontWeight: style.fontWeight,
        }
      };
    },
    render: (value, record) => {

      if (field.name === 'Name') {
        return (
            <a
          href={`/record/${childObjectName}/${record._id}`} target="_self"
        >
          {value}
        </a>
        );
      }


     

      if (field.name === 'recordCount') {
        return null;
      }
      if (field.type === 'lookup' && ((isGlobalSearch || record[`${field.name}`]))) {
        const lookupId = isGlobalSearch ? record[`${field.name}`]?._id : record[`${field.name}`]?._id;
        const lookupName = isGlobalSearch ? record[`${field.name}`]?.Name : record[`${field.name}`]?.Name || 'Loading...';
        const lookupData=isGlobalSearch ? record[`${field.name}`] : record[`${field.name}`];

        if (lookupId && lookupName) {
           return <LookupCell lookupId={lookupData} field={field} ob={field.parent_object_name} />;
        }

      }
      if (field.type === 'address') {
        // Ensure the address is properly constructed from the record
        const address = record[field.name]; // Access the address object
        if (address) {
          // Combine address fields into a single string
          const { street, city, state, country, postal_code } = address;
          return [
            street,
            city,
            state,
            country,
            postal_code,
          ].filter(Boolean).join(', '); // Join non-empty fields with a comma
        }
        return ''; // Return an empty string if the address is not available
      }

      if (field.type === 'multiselect' && value) {
        // Check if text is an array (i.e., the MultiSelect field has multiple values)
        return Array.isArray(value) ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {value?.map((value, index) => (
              <Tag
                key={index}
                color={getUniqueColor(index)} // Get a unique color for each tag
                style={{
                  fontSize: '14px', // Adjust font size for better readability
                  padding: '6px 12px', // Adjust padding for larger size
                  borderRadius: '8px', // Rounded corners for aesthetic appeal
                  lineHeight: '1.5', // Adjust line height for better spacing
                }}
              >
                {value}
              </Tag>
            ))}
          </div>
        ) : (
          value || ''
        );
      }

      if (field.type === 'date' && value) {
        const converteddate=convertDateToUserTimezone(value,DateFormat);
        return converteddate;
      }
      if (field.type === 'datetime' && value) {
        const converteddate=convertDateToUserTimezone(value,DateTimeFormat);
        return converteddate;
      }

      if (field.type === 'phone' && value) {
        // Add PhoneOutlined for phone numbers
        return (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <PhoneOutlined style={{ marginRight: 8 }} />
            {value || ''}
          </div>
        );
      }
      if (field.type === 'currency') {
        const amount = value;
          const fromCode = record?.CurrencyIsoCode;
          const toCode = loginUserCurrency;
        
          if (amount) {
            const formattedAmount = Number(amount).toFixed(2);
            const converted = getConvertedValue(amount, fromCode, toCode);
            const original = `${fromCode} ${formattedAmount}`;
        
            if (fromCode !== toCode) {
              return `${original} (${toCode} ${converted})`;
            } else {
              return original;
            }
          } else {
            return '';
          }
      }

      if (field.type === 'boolean') {
        return <Checkbox checked={value} disabled />;
      }

      if (field.type === 'integer' || field.type === 'decimal') {
        return value === 0 ? '0.00' : value;
      }
      if (field.type === 'multiselect') {
        // Check if text is an array (i.e., the MultiSelect field has multiple values)
        return Array.isArray(value) ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {value?.map((value, index) => (
              <Tag
                key={index}
                color={getUniqueColor(index)} // Get a unique color for each tag
                style={{
                  fontSize: '14px', // Adjust font size for better readability
                  padding: '6px 12px', // Adjust padding for larger size
                  borderRadius: '8px', // Rounded corners for aesthetic appeal
                  lineHeight: '1.5', // Adjust line height for better spacing
                }}
              >
                {value}
              </Tag>
            ))}
          </div>
        ) : (
          value || ''
        );
      }
      if (field.type === 'url') {
        return value ? (
          <a href={value.startsWith('http') ? value : `http://${value}`} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ) : '';
      }
      if (field.type === 'email') {
        return value ? (
          <a href={`mailto:${value}`} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ) : '';
      }

      if (field.type === 'percentage') {
        return value ? `${value}%` : ''; // Format as currency with dollar sign
      }

      return value;
    },
  }));

  if (fieldsToDisplay?.includes('created_at')) {
    columns.push({
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: '180px',
      render: (value, record) => (
        record && dayjs(record.created_at).isValid()
          ? convertDateToUserTimezone(record.created_at, DateTimeFormat)
          : ''
      ),

    });
  }
  if (fieldsToDisplay?.includes('updated_at')) {
    columns.push({
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: '180px',
      render: (value, record) => (
        record && dayjs(record.created_at).isValid()
          ? convertDateToUserTimezone(record.created_at, DateTimeFormat)
          : ''
      ),
    });
  }
  
  const handleEditClick = async (record) => {
    if(security?.object_settings?.[childObjectName]?.object_setting.edit===false){
          message.error('You dont have permission to edit ');
          return;
        }

    try {
      // Fetch fields data
      const apiServiceForFields = new ApiService(
        `${BASE_URL}/mt_fields/object/${childObjectName}`,
        { 'Content-Type': 'application/json' },
        'GET'
      );

      const fieldsResponse = await apiServiceForFields.makeCall();
      const updatedFieldsResponse = fieldsResponse.filter((field) => field.name !== 'recordCount');
      console.log('updatedFieldsResponse', updatedFieldsResponse);
      setFieldsDataState(updatedFieldsResponse);

      // Format the record data
      const formattedRecord = await formatRecordData(record, fieldsResponse, BASE_URL);
      setSelectedRecord(formattedRecord);
      form.setFieldsValue(formattedRecord);
      if (creationView === "Modal") {
        setModalVisible(true);
      }
      else {
        setDrawerVisible(true);
      }

      // Fetch lookup data
      await fetchLookupData(record, fieldsResponse, BASE_URL, setLookupName, form);

      // refreshRecords();
    } catch (error) {
      console.error('Error fetching API response:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloneClick = async (record) => {
     if(security?.object_settings?.[childObjectName]?.object_setting.create===false){
          message.error('You dont have permission to create ');
          return;
        }
    const clonedRecord = { ...record, _id: undefined, isClone: true };

    try {
      // Fetch fields data
      const apiServiceForFields = new ApiService(
        `${BASE_URL}/mt_fields/object/${childObjectName}`,
        { 'Content-Type': 'application/json' },
        'GET'
      );

      const fieldsResponse = await apiServiceForFields.makeCall();
      const updatedFieldsResponse = fieldsResponse.filter((field) => field.name !== 'recordCount');
      setFieldsDataState(updatedFieldsResponse);

      // Format the cloned record data
      const formattedClonedRecord = await formatRecordData(clonedRecord, fieldsResponse, BASE_URL);
      setSelectedRecord(formattedClonedRecord);
      form.setFieldsValue(formattedClonedRecord);

      // Fetch and set lookup data
      await fetchLookupData(clonedRecord, fieldsResponse, BASE_URL, setLookupName, form);

      // refreshRecords(); // Refresh records if needed

    } catch (error) {
      console.error('Error fetching API response:', error);
    } finally {
      setLoading(false);
    }

    if (creationView === "Modal") {
      setModalVisible(true);
    }
    else {
      setDrawerVisible(true);
    } // Open the drawer after setting the values
  };

  const fetchRecords = async () => {
    // Logic to fetch records goes here
    try {
      const apiService = new ApiService(
        `${BASE_URL}/fetch_records/${childObjectName}`,
        {},
        'GET'
      );
      const recordsResponse = await apiService.makeCall();
      setRecords(recordsResponse?.records);
    } catch (error) {
      console.error('Error fetching records:', error);
    }
  };

  const handleFinish = async (values) => {
    console.log('fields',fieldsDataState);
    const updatedValues = generateBody(fieldsDataState, values,(selectedRecord?._id && !selectedRecord?.isClone) ? false : true);
     if(updatedValues==="error"){
        return;
      }
     const changedValues = {};
    
        if(selectedRecord?._id){

          console.log('Selected Record:', selectedRecord);
        
        Object.keys(updatedValues).forEach((key) => {
            const newValue = updatedValues[key];
            const oldValue = selectedRecord[key];
    
            const isNewKey = !(key in selectedRecord);
            const isEmpty = newValue === "" || newValue === null || newValue === undefined;
    
            if (!isEqual(newValue, oldValue)) {
                if (!isNewKey || (isNewKey && !isEmpty)) {
                    changedValues[key] = newValue;
                }
            }
        });
    
        // Remove keys with undefined values
        Object.keys(changedValues).forEach(key => {
          if (changedValues[key] === undefined) {
            delete changedValues[key];
          }
        });
      }

      console.log('Changed Values:', changedValues);
    
    
        
        const body = {
          object_name: childObjectName,
          data: {
            _id: selectedRecord?._id && !selectedRecord?.isClone ? selectedRecord._id : undefined, // If cloning, exclude the ID
            ...((selectedRecord?.isClone || !selectedRecord?._id) ? updatedValues : changedValues)
          }
        };
    

        console.log('Request Body:', body);
  

    try {
      // Create an instance of ApiService for the POST request
      const apiService = new ApiService(
        `${BASE_URL}/insert_or_update_records`,
        { 'Content-Type': 'application/json' },
        'POST',
        body
      );

      await apiService.makeCall();

      message.success(selectedRecord?._id && !selectedRecord?.isClone ? 'Record updated successfully' : 'Record created successfully');
      if (creationView === "Modal") {
        setModalVisible(false);
      }
      else {
        setDrawerVisible(false);
      }
      fetchRecords();
      refreshRecords();
      form.resetFields();
    } catch (error) {
      console.error('Error saving record:', error);

      const errorMessage = error && typeof error === 'object'
        ? Object.entries(error)?.map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`).join(' | ')
        : 'Failed to save record due to an unknown error';
      message.error(errorMessage);
    } finally {
      setLoading(false); // Ensure loading is stopped regardless of success or failure
    }
  };

  const deleteRecord = async (record) => {
     if(security?.object_settings?.[childObjectName]?.object_setting.delete===false){
          message.error('You dont have permission to delete ');
          return;
        }
    try {
      onDelete(childObjectName, record._id); // Call the delete function passed as a prop

      fetchRecords();
    } catch (error) {
      message.error('Failed to delete record.');
    }
  };

  if (!isGlobalSearch) {
    columns.push({
      title: 'Action',
      key: 'operation',
      fixed: 'right',
      width: 130,
      onCell: (record, rowIndex) => {
        const result = getRowStyle ? getRowStyle(record, component) : {};
        const style = result.style || {};
        return {
          style: {
            backgroundColor: style.backgroundColor,
            color: style.color,
            fontWeight: style.fontWeight,
          }
        };
      },
      render: (_, record) => (
        <>
          <Tooltip title="Edit">
            <EditOutlined
              onClick={() => handleEditClick(record)}
              style={{ marginRight: 16, fontSize: '12px', cursor: 'pointer' }}
            />
          </Tooltip>
          <Tooltip title="Clone">
            <CopyOutlined
              onClick={() => handleCloneClick(record)}
              style={{ marginRight: 16, fontSize: '12px', cursor: 'pointer' }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Popconfirm
              title="Are you sure you want to delete this record?"
              onConfirm={() => deleteRecord(record)}
            >
              <DeleteOutlined
                style={{ color: 'red', fontSize: '12px', cursor: 'pointer' }}
              />
            </Popconfirm>
          </Tooltip>
        </>
      ),
    });
  }
  const handleViewAllClick = async () => {

    // Replace this URL with your API endpoint to fetch data
    if (isGlobalSearch) {
      setLoadingData(true);
      try {
        const apiService = new ApiService(`${BASE_URL}/search_specific/${childObjectName}/${keyword}/10/0`, {
          'Content-Type': 'application/json', // Add any necessary headers, such as content type
        }, 'GET',);
        const response = await apiService.makeCall();
        if (response) {
          setResponseData(response.records);  // Store the fetched data

          navigate('/ViewAllRecords',
            { state: { objName: response.object_name, results: response.records, metadata: fieldsData, keyword: keyword, total: response.total, isGlobalSearch: isGlobalSearch } }
          );
        } else {
          message.error('Failed to fetch data');
        }
      } catch (error) {
        message.error('An error occurred while fetching data');
      } finally {
        setLoadingData(false);
      }
    }
    else {
      setLoadingData(true);
    }

  };

const tableComponents = {
  body: {
    row: (props) => {
      const { className, children, ...restProps } = props;
      const record = props['data-row-key']
        ? (childRecords || responseData || []).find(r => r._id === props['data-row-key'])
        : null;
      // Get the highlight message for this row
      const result = getRowStyle ? getRowStyle(record, component) : {};
      const highlightMessage = result.message;
      const highlightStyle = result.style || {};

      if (highlightMessage) {
        return (
          <Tooltip
            title={highlightMessage}
            placement="top"
            overlayInnerStyle={{
              background: highlightStyle.backgroundColor || undefined,
              border: highlightStyle.border || undefined,
              color: highlightStyle.color || undefined,
              borderRadius: 4,
              fontWeight: highlightStyle.fontWeight || undefined,
            }}
            overlayStyle={{
              borderRadius: 4,
            }}
          >
            <tr {...restProps} className={className}>
              {children}
            </tr>
          </Tooltip>
        );
      }
      return <tr {...restProps} className={className}>{children}</tr>;
    }
  }
};

  return (
    <Spin spinning={loadingData}>

      <div style={{ minHeight: '285px' }}>
        <Table
          dataSource={childRecords || []}
          columns={columns}
          rowKey="_id"
          pagination={tableProperties?.pagination ? {
            ...pagination,
            pageSize: tableProperties.pagination.pageSize,
            showSizeChanger: tableProperties.pagination.showSizeChanger,
            showQuickJumper: tableProperties.pagination.showQuickJumper,
            showTotal: tableProperties.pagination.showTotal ? 
              (total, range) => `${range[0]}-${range[1]} of ${total} items` : 
              false,
            position: tableProperties.pagination.position,
            pageSizeOptions: tableProperties.pagination.pageSizeOptions
          } : pagination}
          loading={tableProperties?.loading !== undefined ? tableProperties.loading : (childRecords === null || childRecords === undefined)}
          scroll={
            tableProperties?.scroll 
              ? tableProperties.scroll 
              : (childRecords && childRecords.length > 0 
                  ? { x: 'scroll', y: 240 } 
                  : undefined)
          }
          size={tableProperties?.size || 'middle'}
          bordered={tableProperties?.bordered || false}
          showHeader={tableProperties?.showHeader !== false}
          sticky={tableProperties?.sticky || false}
          virtual={tableProperties?.virtual || false}
          rowSelection={tableProperties?.rowSelection ? {} : false}
          onRow={(record, index) => {
            const result = getRowStyle ? getRowStyle(record, component) : {};
            return { style: result.style || {} };
          }}
          rowClassName={(record, index) => {
            // Base row classes
            let baseClasses = [];
            
            // Apply striped styling if enabled
            if (tableProperties?.striped && index % 2 !== 0) {
              baseClasses.push('striped-row');
            }
            
            // Apply cell padding classes
            if (tableProperties?.cellPadding === 'compact') {
              baseClasses.push('compact-padding');
            } else if (tableProperties?.cellPadding === 'comfortable') {
              baseClasses.push('comfortable-padding');
            }
            return baseClasses.join(' ');
          }}
          components={tableComponents}
          style={{
            ...tableProperties?.headerStyle && {
              '--header-bg': tableProperties.headerStyle.backgroundColor,
              '--header-weight': tableProperties.headerStyle.fontWeight || 'bold'
            }
          }}
        />
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          {/* <Divider style={{ margin: '16px 0', borderTop: '1px solid #000' }} /> */}
          {/* <hr></hr> */}
          {isGlobalSearch && (<Button
            type="link"
            onClick={handleViewAllClick}
            loading={loadingData}
            style={{
              // fontWeight: 'bold',
              textDecoration: 'none',
            }}
            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} // Apply underline on hover
            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}

          >
            View All
          </Button>)}
        </div>

        {/* If additional data is fetched, display it in another table */}
        {responseData.length > 0 && (
          <Table
            dataSource={responseData}
            columns={columns} // You can adjust columns if needed for the new data
            rowKey="_id"
            pagination={false}
            loading={loadingData || isLoading}
            scroll={{
              x: 'auto',
            }}
            style={{ marginTop: 24 }}
            rowClassName={(record, index) => {
              // Get dynamic row CSS class from getRowStyle function
              const dynamicClass = getRowStyle ? getRowStyle(record, component) : '';
                console.log('Row className for record:', record, 'is', dynamicClass);
              
              // Base row classes
              let baseClasses = [];
              
              // Apply striped styling if enabled
              if (tableProperties?.striped && index % 2 !== 0) {
                baseClasses.push('striped-row');
              }
              
              // Apply cell padding classes
              if (tableProperties?.cellPadding === 'compact') {
                baseClasses.push('compact-padding');
              } else if (tableProperties?.cellPadding === 'comfortable') {
                baseClasses.push('comfortable-padding');
              }
              
              // Combine base classes with dynamic highlight class
              const allClasses = [...baseClasses, dynamicClass].filter(Boolean);
              return allClasses.join(' ');
            }}
            components={tableComponents}
          />
        )}
        {/* {responseData && isGlobalSearch && <ListViewTable
        fieldsData={columns}
        childRecords={responseData}
      />
    
    } */}
        {(drawerVisible &&
          <CreateRecordDrawer
            visible={drawerVisible}
            onClose={() => setDrawerVisible(false)}
            onFinish={handleFinish}
            loading={loading}
            objectName={childObjectName}
            fieldsData={fieldsDataState}
            selectedRecord={selectedRecord}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            form={form}
          />
        )}
        {(modalVisible &&
          <CreateRecordModal
            visible={modalVisible}
            onClose={() => setModalVisible(false)}
            onFinish={handleFinish}
            loading={loading}
            objectName={childObjectName}
            fieldsData={fieldsDataState}
            selectedRecord={selectedRecord}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            form={form}
            // layout={layout}
          />
        )}
      </div>
    </Spin>

  );
};

export default ChildRecordTable;
